/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	type ComponentPropsWithoutRef,
	type DetailedHTMLProps,
	forwardRef,
	type ForwardRefExoticComponent,
	type HTMLAttributes,
	type ImgHTMLAttributes,
	type RefAttributes,
} from 'react';

import { cssMap as unboundCssMap, jsx } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

// Cannot use Box: <img> element is not supported as an `as` prop in @atlaskit/primitives Box
const errorImageStyles = unboundCssMap({
	root: {
		width: '263px',
		marginTop: token('space.0', '0px'),
		marginRight: token('space.0', '0px'),
		marginBottom: token('space.600', '48px'),
		marginLeft: token('space.0', '0px'),
		display: 'flex',
	},
});

export const ErrorImage: ForwardRefExoticComponent<
	Omit<DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>, 'ref'> &
		RefAttributes<HTMLImageElement>
> = forwardRef<HTMLImageElement, ComponentPropsWithoutRef<'img'>>(({ alt = '', ...props }, ref) => (
	<img ref={ref} alt={alt} css={errorImageStyles.root} {...props} />
));

// Cannot use Box/xcss: media queries are not supported in @atlaskit/css cssMap
const containerStyles = unboundCssMap({
	root: {
		alignItems: 'center',
		justifyContent: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		marginTop: token('space.1000', '80px'),
		marginRight: 'auto',
		marginBottom: token('space.0', '0px'),
		marginLeft: 'auto',
		maxWidth: '600px',
		flexDirection: 'column',
		'@media (max-width: 640px)': {
			flexDirection: 'column',
		},
	},
});

export const Container: ForwardRefExoticComponent<
	Omit<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'ref'> &
		RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>((props, ref) => (
	<div ref={ref} css={containerStyles.root} {...props} />
));

// Cannot use Box/xcss: media queries and nested selectors are not supported in @atlaskit/css cssMap
const imageOuterStyles = unboundCssMap({
	root: {
		flex: 'none',
		flexBasis: '200px',
		justifyContent: 'center',
		'@media (max-width: 640px)': {
			marginRight: token('space.0', '0px'),
			flexBasis: 'auto',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Child img selector required for responsive padding
			'& img': {
				maxWidth: '100%',
				paddingTop: token('space.0', '0px'),
				paddingRight: token('space.300', '24px'),
				paddingBottom: token('space.0', '0px'),
				paddingLeft: token('space.300', '24px'),
			},
		},
	},
});

export const ImageOuter: ForwardRefExoticComponent<
	Omit<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'ref'> &
		RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>((props, ref) => (
	<div ref={ref} css={imageOuterStyles.root} {...props} />
));

// Cannot use Box/xcss: media queries are not supported in @atlaskit/css cssMap
const titleWrapperStyles = unboundCssMap({
	root: {
		display: 'flex',
		marginBottom: token('space.150', '12px'),
		'@media (max-width: 640px)': {
			textAlign: 'center',
		},
	},
});

export const TitleWrapper: ForwardRefExoticComponent<
	Omit<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'ref'> &
		RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>((props, ref) => (
	<div ref={ref} css={titleWrapperStyles.root} {...props} />
));

const buttonContainerStyles = cssMap({
	root: {
		display: 'flex',
		justifyContent: 'center',
		textAlign: 'center',
		marginTop: token('space.200'),
	},
});

export const ButtonContainer: ForwardRefExoticComponent<
	Omit<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'ref'> &
		RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>((props, ref) => (
	<Box ref={ref} xcss={buttonContainerStyles.root} {...props} />
));

const descriptionWrapperStyles = cssMap({
	root: {
		display: 'flex',
		flexWrap: 'wrap',
	},
});

export const DescriptionWrapper: ForwardRefExoticComponent<
	Omit<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'ref'> &
		RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>((props, ref) => (
	<Box ref={ref} xcss={descriptionWrapperStyles.root} {...props} />
));

// Cannot use Box/xcss: media queries are not supported in @atlaskit/css cssMap
const descriptionStyles = unboundCssMap({
	root: {
		font: token('font.body'),
		color: token('color.text'),
		'@media (max-width: 640px)': {
			textAlign: 'center',
		},
	},
});

export const Description: ForwardRefExoticComponent<
	Omit<DetailedHTMLProps<HTMLAttributes<HTMLParagraphElement>, HTMLParagraphElement>, 'ref'> &
		RefAttributes<HTMLParagraphElement>
> = forwardRef<HTMLParagraphElement, ComponentPropsWithoutRef<'p'>>((props, ref) => (
	<p ref={ref} css={descriptionStyles.root} {...props} />
));

// Cannot use Box/xcss: media queries are not supported in @atlaskit/css cssMap
const errorTextContainerStyles = unboundCssMap({
	root: {
		display: 'flex',
		justifyContent: 'center',
		textAlign: 'center',
		flexWrap: 'wrap',
		width: '80%',
		flexDirection: 'column',
		alignItems: 'center',
		'@media (max-width: 640px)': {
			maxWidth: '304px',
			width: '100%',
		},
	},
});

export const ErrorTextContainer: ForwardRefExoticComponent<
	Omit<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'ref'> &
		RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>((props, ref) => (
	<div ref={ref} css={errorTextContainerStyles.root} {...props} />
));
